import React from 'react';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import GoogleMapReact, { Coords } from 'google-map-react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { TooltipButton } from '../tooltip-button';
import MapUtils from '../../utils/mapUtils';
import FormUtils from '../../utils/forms';
import { clsx } from '../../utils/htmlUtils';
import envConfig from '../../config';

//=======================================//
//=============== Setting ===============//
//=======================================//

const SETTING_FIT_DELETE = false;
const SETTING_FIT_ADD = false;
const SETTING_FIT_MIN = 2;

interface GoogleMapSelectPolygonProps extends WithTranslation {
    globalClasses: any;
    center: Coords;
    zoom: number;
    polygon: Array<Coords>;
    parentPolygon?: Array<Coords>;
    onPolygonChange: (polygon: Array<Coords>) => void;
    className?: string;
}

interface GoogleMapSelectPolygonState {
    addIndex: number;
    formPoint: {
        lat: number | null;
        lng: number | null;
    };
}

class GoogleMapSelectPolygon extends React.Component<GoogleMapSelectPolygonProps, GoogleMapSelectPolygonState> {
    static defaultProps = {
        center: MapUtils.defaultCenter,
        zoom: MapUtils.defaultZoom,
    };

    map: any;
    maps: any;
    polygon: any;
    markers: any[] = [];
    defaultBounds: any;
    markerHint: any;

    state = {
        addIndex: -1,
        formPoint: {
            lat: null,
            lng: null,
        },
    };

    handleApiLoaded = (map: any, maps: any) => {
        this.map = map;
        this.maps = maps;

        const { center, polygon, parentPolygon } = this.props;

        this.defaultBounds = MapUtils.getBounds(maps, [center]);

        if (parentPolygon?.length) {
            const parentPolyg = MapUtils.generatePolygon(maps, parentPolygon, '#00FF00', '#9ACD32');
            parentPolyg.setMap(map);
            parentPolyg.addListener('click', (e: any) => this.onClickHandler(e));
            this.defaultBounds = MapUtils.getBounds(maps, parentPolygon);
            map.fitBounds(this.defaultBounds);
        }

        if (polygon?.length >= 2) {
            const bounds = MapUtils.getBounds(maps, polygon);
            this.polygon = MapUtils.createEditablePolygon(maps, map, polygon, this.markers, this.onUpdatePoint);
            map.fitBounds(bounds);
        }

        maps.event.addListener(map, 'click', (e: any) => this.onClickHandler(e));
    };

    onUpdatePoint = () => {
        if (!this.polygon) return;

        // Get the actual updated coordinates from the polygon
        const path = this.polygon.getPath();
        const updatedPolygon = [];

        for (let i = 0; i < path.getLength(); i++) {
            const point = path.getAt(i);
            updatedPolygon.push({
                lat: point.lat(),
                lng: point.lng(),
            });
        }

        // Notify parent of the actual polygon coordinates
        this.props.onPolygonChange(updatedPolygon);
    };

    onClickHandler(e: any) {
        const { maps, map } = this;
        const { polygon } = this.props;
        const { addIndex } = this.state;
        const bounds = MapUtils.getBounds(maps, polygon);

        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        const newPolygon = [...polygon];

        if (addIndex > -1) {
            newPolygon.splice(addIndex, 0, { lat, lng });
        } else {
            newPolygon.push({ lat, lng });
        }
        bounds.extend(e.latLng);

        if (this.polygon) {
            this.polygon.setMap(null);
        }

        this.polygon = MapUtils.createEditablePolygon(maps, map, newPolygon, this.markers, this.onUpdatePoint);
        this.polygon.addListener('click', (event: any) => this.onClickHandler(event));

        if (SETTING_FIT_ADD) {
            if (polygon.length >= SETTING_FIT_MIN) {
                map.fitBounds(bounds);
            }
        }

        this.props.onPolygonChange(newPolygon);
        this.setState({ addIndex: -1 });
    }

    handleDeletePoint = (index: number) => () => {
        const { polygon } = this.props;
        const { maps, map } = this;

        // Remove hint marker if it exists
        if (this.markerHint) {
            MapUtils.removeMarker(this.markerHint);
            this.markerHint = null;
        }

        const newPolygon = [...polygon];
        newPolygon.splice(index, 1);

        // Clean up existing polygon
        if (this.polygon) {
            this.polygon.setMap(null);
            this.polygon = null;
        }

        // Create new polygon with updated points
        const newPolygonRef = MapUtils.createEditablePolygon(maps, map, newPolygon, this.markers, this.onUpdatePoint);

        // Only create new polygon if we have at least 2 points
        if (newPolygon.length >= 2) {
            this.polygon = newPolygonRef;
            if (SETTING_FIT_DELETE) {
                const bounds = MapUtils.getBounds(maps, newPolygon);
                map.fitBounds(bounds);
            }
        }

        this.props.onPolygonChange(newPolygon);
    };

    handleSelectIndexAdd = (index: number) => () => {
        this.setState({ addIndex: index });
    };

    handleSubmitPoint = (event?: React.SyntheticEvent) => {
        const { formPoint } = this.state;
        event?.preventDefault();
        event?.stopPropagation();

        if (this.maps && this.map && formPoint.lat !== null && formPoint.lng !== null) {
            const event = {
                latLng: new this.maps.LatLng(formPoint.lat, formPoint.lng),
            };
            this.onClickHandler(event);

            this.setState({
                formPoint: { lat: null, lng: null },
            });
        }
    };

    handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { formPoint } = this.state;
        this.setState({
            formPoint: FormUtils.nextSubState(event, formPoint),
        });
    };

    onShowMarker = (p: Coords) => () => {
        const { maps, map } = this;
        if (!this.markerHint) {
            this.markerHint = MapUtils.addMarker(maps, map, p);
        }
    };

    onHideMarker = () => () => {
        MapUtils.removeMarker(this.markerHint);
        this.markerHint = null;
    };

    render() {
        const { t, globalClasses, center, zoom, polygon, className } = this.props;
        const { addIndex, formPoint } = this.state;

        return (
            <div className={className}>
                <p>{addIndex > -1 && t('areas.add_index', { index: addIndex + 1 })}</p>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}>
                    <span style={{ margin: 'auto' }}>{t('common:coord.lat')}</span>
                    <FormControl margin="dense">
                        <Input type="number" value={formPoint.lat ?? ''} name="lat" onChange={this.handleChange} />
                    </FormControl>
                    <span style={{ margin: 'auto' }}>{t('common:coord.lng')}</span>
                    <FormControl margin="dense">
                        <Input type="number" value={formPoint.lng ?? ''} name="lng" onChange={this.handleChange} />
                    </FormControl>
                    <TooltipButton title={t('common:add')}>
                        <Button
                            type="button"
                            variant="contained"
                            color={formPoint.lat !== null && formPoint.lng !== null ? 'secondary' : 'inherit'}
                            onClick={this.handleSubmitPoint}
                        >
                            <Icon fontSize="small">add</Icon>
                        </Button>
                    </TooltipButton>
                </div>

                <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 16 }}>
                    <div style={{ marginRight: 16, minWidth: 276 }}>
                        <b>{t('areas.polygon')}</b>
                        {!polygon?.length && <p>{t('areas.polygon_empty')}</p>}
                        {polygon?.map((p, index) => (
                            <div
                                key={`${p.lat}${p.lng}${index}`}
                                style={{ marginBottom: 4, display: 'flex', alignItems: 'center' }}
                                onMouseOver={this.onShowMarker(p)}
                                onMouseLeave={this.onHideMarker()}
                            >
                                <TooltipButton title={t('areas.add_before')}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        style={{ marginRight: 8 }}
                                        onClick={this.handleSelectIndexAdd(index)}
                                        className={clsx(globalClasses.btnAutoWidth, globalClasses.btnSuccess)}
                                    >
                                        <Icon fontSize="small">add</Icon>
                                    </Button>
                                </TooltipButton>
                                <span style={{ marginRight: 8 }}>{`${p.lat.toFixed(6)} ${p.lng.toFixed(6)}`}</span>
                                <TooltipButton title={t('common:delete')}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        style={{ marginRight: 8 }}
                                        onClick={this.handleDeletePoint(index)}
                                        className={clsx(globalClasses.btnAutoWidth, globalClasses.btnDanger)}
                                    >
                                        <Icon fontSize="small">delete</Icon>
                                    </Button>
                                </TooltipButton>
                                <TooltipButton title={t('areas.add_after')}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={this.handleSelectIndexAdd(index + 1)}
                                        className={clsx(globalClasses.btnAutoWidth, globalClasses.btnSuccess)}
                                    >
                                        <Icon fontSize="small">add</Icon>
                                    </Button>
                                </TooltipButton>
                            </div>
                        ))}
                    </div>
                    <div style={{ flex: 1, minHeight: '50vh' }}>
                        <GoogleMapReact
                            bootstrapURLKeys={{ key: envConfig.google_key }}
                            yesIWantToUseGoogleMapApiInternals
                            onGoogleApiLoaded={({ map, maps }) => this.handleApiLoaded(map, maps)}
                            defaultCenter={center}
                            defaultZoom={zoom}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default withTranslation(['pricing', 'common'])(GoogleMapSelectPolygon);
